import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { SiteLayout } from '@/components/SiteLayout';
import { Button } from '@/components/ui/button';
import { CATEGORIES, TOTAL_PROBLEMS, type Category } from '@/data/problems';
import { progressKey, useProgress } from '@/hooks/useProgress';
import { cn } from '@/lib/utils';

const LEVEL_LABEL: Record<Category['level'], string> = {
  1: 'Level 1 — No loops',
  2: 'Level 2 — Loops & methods',
  3: 'Level 3 — Recursion',
};

function CategoryCard({ category }: { category: Category }) {
  const { progress } = useProgress();
  const solved = category.problems.filter((p) => progress[progressKey(category.id, p.id)]?.solved).length;
  const total = category.problems.length;
  const pct = Math.round((solved / total) * 100);
  const done = solved === total;

  return (
    <Link
      to={`/problems/${category.id}`}
      className={cn(
        'group relative flex flex-col gap-3 rounded-xl border bg-card p-5 shadow-sm transition-all',
        'hover:-translate-y-0.5 hover:border-brand hover:shadow-md motion-reduce:transform-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-mono text-lg font-bold tracking-tight">{category.name}</h3>
        {done ? (
          <CheckCircle2 className="size-5 text-success" aria-label="Completed" />
        ) : (
          <span className="font-mono text-xs text-muted-foreground">
            {solved}/{total}
          </span>
        )}
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">{category.blurb}</p>
      <div className="mt-auto h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
        <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${pct}%` }} />
      </div>
    </Link>
  );
}

const Index = () => {
  useSeoMeta({
    title: 'JSBat — JavaScript Coding Practice',
    description: 'Free, bite-sized JavaScript coding problems with instant feedback. Write a function, run the tests, and build real skill — right in your browser.',
  });

  const levels = [1, 2, 3] as const;
  const first = CATEGORIES[0];

  return (
    <SiteLayout>
      <section className="relative isolate overflow-hidden border-b">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,var(--brand-glow),transparent_60%)]" />
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,var(--grid-line)_1px,transparent_1px),linear-gradient(to_bottom,var(--grid-line)_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:linear-gradient(to_bottom,black,transparent)]" />
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 md:py-24 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border bg-background/60 px-3 py-1 font-mono text-xs text-muted-foreground">
              <span className="size-2 rounded-full bg-success" /> {TOTAL_PROBLEMS} problems · runs in your browser
            </p>
            <h1 className="text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
              Get good at JavaScript,
              <br />
              <span className="text-brand-strong">one function</span> at a time.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
              Short coding problems with instant feedback. Read the prompt, write a function, hit Go, and see
              exactly which tests pass. No sign-up, no setup — your progress saves automatically.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-brand text-brand-foreground hover:bg-brand/90">
                <Link to={`/problems/${first.id}/${first.problems[0].id}`}>
                  Start with {first.problems[0].fn}() <ArrowRight />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/help">How it works</Link>
              </Button>
            </div>
          </div>

          <div className="rounded-xl border bg-code-bg font-mono text-[15px] leading-7 text-code-fg shadow-xl">
            <div className="flex items-center gap-1.5 border-b border-code-border px-4 py-3">
              <span className="size-3 rounded-full bg-red-400/80" />
              <span className="size-3 rounded-full bg-amber-400/80" />
              <span className="size-3 rounded-full bg-emerald-400/80" />
              <span className="ml-3 text-xs text-code-comment">Warmup-1 / sleepIn</span>
            </div>
            <pre className="overflow-x-auto p-5">
              <span className="text-code-keyword font-semibold">function</span> <span className="text-code-fn">sleepIn</span>
              <span className="text-code-punct">(</span>weekday<span className="text-code-punct">,</span> vacation<span className="text-code-punct">)</span> <span className="text-code-punct">{'{'}</span>
              {'\n  '}<span className="text-code-keyword font-semibold">return</span> <span className="text-code-punct">!</span>weekday <span className="text-code-punct">||</span> vacation<span className="text-code-punct">;</span>
              {'\n'}<span className="text-code-punct">{'}'}</span>
            </pre>
            <div className="space-y-1 border-t border-code-border px-5 py-4 text-sm">
              {[
                ['sleepIn(false, false)', 'true'],
                ['sleepIn(true, false)', 'false'],
                ['sleepIn(false, true)', 'true'],
              ].map(([call, out]) => (
                <div key={call} className="flex items-center justify-between gap-4">
                  <span>
                    {call} <span className="text-code-comment">→</span> {out}
                  </span>
                  <span className="rounded bg-emerald-500/20 px-2 text-xs font-semibold text-emerald-300">OK</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl space-y-14 px-4 py-14 sm:px-6">
        {levels.map((level) => (
          <section key={level} aria-labelledby={`level-${level}`}>
            <h2 id={`level-${level}`} className="mb-6 text-2xl font-bold tracking-tight">
              {LEVEL_LABEL[level]}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {CATEGORIES.filter((c) => c.level === level).map((c) => (
                <CategoryCard key={c.id} category={c} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </SiteLayout>
  );
};

export default Index;
